import { useCallback } from "react";
import { useUpdateFacebookConnectionMutation } from "@services/customer/mutations";
import { waitForVar } from "@utils/waitForVar";
import {
  FACEBOOK_APP_ID,
  FACEBOOK_SCRIPT_URL,
  FACEBOOK_SDK_VERSION,
} from "@/constants";
import { useScript } from "./useScript";

interface IFacebookLoginResponse {
  status: string;
  authResponse: {
    accessToken: string;
    data_access_expiration_time: number;
    expiresIn: number;
    graphDomain: string;
    signedRequest: string;
    userID: string;
  };
}

interface IFacebookSDK {
  init: (params: { appId: string; version: string }) => void;
  login: (callback: (response: IFacebookLoginResponse) => void) => void;
  getLoginStatus: (
    callback: (response: IFacebookLoginResponse) => void,
  ) => void;
}

export const useFacebookAuth = () => {
  useScript(FACEBOOK_SCRIPT_URL);

  const { mutateAsync: updateFacebookConnection } =
    useUpdateFacebookConnectionMutation();

  const getFacebookInterfaceResponse = async () => {
    const facebookSDK = (await waitForVar("FB")) as IFacebookSDK;

    facebookSDK.init({
      appId: FACEBOOK_APP_ID,
      version: FACEBOOK_SDK_VERSION,
    });

    const loginResponse = await new Promise<IFacebookLoginResponse>((resolve) =>
      facebookSDK.login(resolve),
    );

    return loginResponse;
  };

  const getFacebookAccessToken = useCallback(async (): Promise<
    string | undefined
  > => {
    const facebookSDK = (await waitForVar("FB")) as IFacebookSDK;

    facebookSDK.init({
      appId: FACEBOOK_APP_ID,
      version: FACEBOOK_SDK_VERSION,
    });

    const loginStatusResponse = await new Promise<IFacebookLoginResponse>(
      (resolve) => facebookSDK.getLoginStatus(resolve),
    );

    if (loginStatusResponse.status === "connected") {
      return loginStatusResponse.authResponse.accessToken;
    }

    return undefined;
  }, []);

  const handleFacebookAuth = async () => {
    const facebookResponse = await getFacebookInterfaceResponse();

    await updateFacebookConnection({
      facebookAccessToken: facebookResponse.authResponse.accessToken,
    });
  };

  const handleFacebookDeauth = async () => {
    await updateFacebookConnection({
      facebookAccessToken: null,
    });
  };

  return {
    handleFacebookAuth,
    handleFacebookDeauth,
    getFacebookInterfaceResponse,
    getFacebookAccessToken,
  };
};
